// Feedback list (handoff 7.3). AppBar + status filter (native radiogroup) + rows.
// Mobile: sticky BottomActionBar "提交反馈". Desktop: inline CTA. Empty -> CTA.

import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { AppBar } from '../components/AppBar';
import { StatusBadge } from '../components/StatusBadge';
import { EmptyState } from '../components/EmptyState';
import { ListSkeleton } from '../components/Skeletons';
import { BottomActionBar } from '../components/BottomActionBar';
import { ShareButton } from '../components/ShareButton';
import { Plus, Warning } from '../lib/icons';
import { api, ApiError } from '../lib/api';
import { getSession } from '../lib/session';
import { relativeTime } from '../lib/format';
import { useScreenEnter } from '../lib/motion';
import type { Feedback, FeedbackStatus } from '../lib/types';

type FilterKey = 'all' | 'active' | 'waiting' | 'resolved';

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'active', label: '处理中' },
  { key: 'waiting', label: '待补充' },
  { key: 'resolved', label: '已解决' },
];

const FILTER_SET: Record<FilterKey, Set<FeedbackStatus>> = {
  all: new Set([
    'submitted',
    'under_review',
    'awaiting_info',
    'in_progress',
    'resolved',
    'confirmed',
    'reopened',
  ]),
  active: new Set(['submitted', 'under_review', 'in_progress', 'reopened']),
  waiting: new Set(['awaiting_info']),
  resolved: new Set(['resolved', 'confirmed']),
};

export function List() {
  const navigate = useNavigate();
  const session = getSession();
  const [items, setItems] = useState<Feedback[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [filter, setFilter] = useState<FilterKey>('all');

  const load = () => {
    setError(null);
    setItems(null);
    api
      .listFeedback()
      .then(setItems)
      .catch((e: ApiError) => setError(e));
  };

  useEffect(load, []);

  const context = session ? `${session.org} · ${session.project}` : undefined;
  const visible = items ? items.filter((f) => FILTER_SET[filter].has(f.status)) : [];

  return (
    <>
      <AppBar title="我的反馈" context={context} trailing={<ShareButton />} />
      <main className="app-main">
        <motion.section className="section" {...useScreenEnter()} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <fieldset className="filter-group" aria-label="按状态筛选">
            <legend className="sr-only">按状态筛选</legend>
            {FILTERS.map((f) => (
              <div className="filter-pill" key={f.key}>
                <input
                  type="radio"
                  name="status-filter"
                  id={`filter-${f.key}`}
                  checked={filter === f.key}
                  onChange={() => setFilter(f.key)}
                />
                <label htmlFor={`filter-${f.key}`}>{f.label}</label>
              </div>
            ))}
          </fieldset>

          {error && (
            <div className="error-banner" role="alert">
              <Warning weight="regular" aria-hidden="true" />
              <span>{error.message}</span>
              <button type="button" className="btn btn-ghost" style={{ height: 32, marginLeft: 'auto' }} onClick={load}>
                重试
              </button>
            </div>
          )}

          {items === null && !error && <ListSkeleton rows={5} />}

          {items && items.length === 0 && (
            <EmptyState
              title="还没有反馈"
              guidance="提交第一条反馈，我们会记录并跟踪处理进展。"
              ctaLabel="提交第一条反馈"
              onCta={() => navigate('/new')}
            />
          )}

          {items && items.length > 0 && visible.length === 0 && (
            <p className="muted" style={{ textAlign: 'center', padding: '2rem 0' }}>
              该状态下暂无反馈。
            </p>
          )}

          {visible.length > 0 && (
            <div className="list" role="list">
              {visible.map((f) => (
                <Link key={f.id} to={`/${f.id}`} className="row" role="listitem">
                  <div className="row-title">{f.summary}</div>
                  <div className="row-meta">
                    <StatusBadge status={f.status} />
                    <span className="row-time">{relativeTime(f.updatedAt)}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Desktop inline CTA (mobile uses the bottom action bar below). */}
          {items && items.length > 0 && (
            <div className="hidden md:flex md:justify-end">
              <button type="button" className="btn btn-primary" onClick={() => navigate('/new')}>
                <Plus weight="regular" aria-hidden="true" />
                提交反馈
              </button>
            </div>
          )}
        </motion.section>
      </main>

      {items && items.length > 0 && (
        <BottomActionBar>
          <button type="button" className="btn btn-primary btn-block md:hidden" onClick={() => navigate('/new')}>
            <Plus weight="regular" aria-hidden="true" />
            提交反馈
          </button>
        </BottomActionBar>
      )}
    </>
  );
}
